import { memo, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import { RadioGroup } from '@library';
import CollapsibleContent from '../../../core/components/CollapsibleContent';
import CollapsibleSideBarFilterTitle from '../ProductList/Filters/CollapsibleSideBarFilterTitle';
import { useProductListOptionsContext } from '../ProductList/context/ProductListOptionsContext';
import withErrorBoundary from '../../../core/components/errors/withErrorBoundary';
import useCurrentStockOnlyFlag from '../../../availability/hooks/useCurrentStockOnlyFlag';
import useMainStockLocation from '../../../core/hooks/useMainStockLocation';
import usePagination from '../../hooks/usePagination';

const baseAvailabilityFilterOptions = [
    {
        value: '',
        label: <FormattedMessage id="all" />,
    },
    {
        value: '1',
        label: <FormattedMessage id="immediately" />,
    },
];

const FilterAvailability = ({ defaultExpand }: { defaultExpand?: boolean }) => {
    const [searchParams] = useSearchParams();
    const {
        filters: { bestAvailability },
    } = useProductListOptionsContext();
    const currentStockOnly = useCurrentStockOnlyFlag();
    const stockLocation = useMainStockLocation();
    const intl = useIntl();

    const { navigateToPage } = usePagination();
    const availabilityFilterOptions = useMemo(() => {
        return [
            ...baseAvailabilityFilterOptions,
            ...(currentStockOnly
                ? []
                : [
                      {
                          value: '7',
                          label: <FormattedMessage id="within 1 week" />,
                      },
                      {
                          value: '14',
                          label: <FormattedMessage id="within 2 weeks" />,
                      },
                      {
                          value: '28',
                          label: <FormattedMessage id="within 1 month" />,
                      },
                  ]),
        ];
    }, [currentStockOnly]);

    if (!stockLocation) {
        return null;
    }

    const onUpdate = (value: string) => {
        if (!value) {
            searchParams.delete('bestAvailability');
        } else {
            searchParams.set('bestAvailability', value);
        }
        navigateToPage(1, searchParams.toString());
    };

    return (
        <CollapsibleContent
            expanded={defaultExpand}
            smallIcon
            title={
                <CollapsibleSideBarFilterTitle>
                    {intl.formatMessage({
                        id: 'availability',
                    })}
                </CollapsibleSideBarFilterTitle>
            }
            endArrow
            filterIcon
        >
            <RadioGroup
                secondaryLabel
                name="availabilitySidebarFilter"
                value={`${bestAvailability?.value ?? ''}`}
                options={availabilityFilterOptions}
                onChange={onUpdate}
                direction="column"
            />
        </CollapsibleContent>
    );
};

export default memo(withErrorBoundary(FilterAvailability));
